import { CellValueChangedEvent, GetRowIdParams, GridReadyEvent } from '@ag-grid-community/core';
import { HttpClient } from '@angular/common/http';
import { Component } from '@angular/core';
import { environment } from 'environments/environment';
import _ from 'lodash';
import { firstValueFrom } from 'rxjs';

import { Account, JoDetail, JoHeader } from '@/model/entities/entity-model';
import { AgFns, GridOptionsExt } from '@/shared/ag-fns';
import { DomainBaseComponent, wrapBusy } from '@/shared/domain-base.component';
import { DomainService } from '@/shared/domain.service';
import { NavFns } from '@/shared/nav-fns';
import { UtilFns } from '@/shared/util-fns';

/** One priceable row: a distinct ProductType across the SELECTED orders. */
interface ProductTypeRow {
  productTypeId: number;
  manufacturerName: string;
  style: string;
  description: string;
  lineCt: number;
  unitCt: number;
  currentReissueAmt: number;
  isInProgram: boolean;
  newReissueAmt: number | null;
}

/**
 * Administration > Utilities > Enable Account Inventory.
 *
 * Repairs orders submitted to the supplier BEFORE their account's inventory program
 * existed in Proximity. Proximity stamps JoHeader.AllowInventory, InventoryProgramName,
 * ChargeAddonsOnReissuance and each JoDetail.UnitReissueAmt once, at submission, and
 * never re-sends them — AddJoHeader rejects re-sends. Such an order can never draw on
 * its own account's stock, and if it did, ship-prep would bill those units at
 * UnitReissueAmt = $0.00.
 *
 * All four of those fields are columns on JoHeader/JoDetail — Uniformax has no
 * account-level equivalent (Proximity holds that: SupplierAccount.ChargeAddonsOnReissuance,
 * ProgramSupplier.AllowInventory, PricedProductType.ReissuancePrice). So the page collects
 * each setting ONCE and writes a copy onto every order the operator selects.
 *
 * Flow: pick account -> tick orders -> price the styles those orders actually contain
 * -> Apply. See docs/account-owned-inventory-flow.md Rule 10.
 */
@Component({
  selector: 'app-enable-account-inventory',
  templateUrl: './enable-account-inventory.component.html',
  standalone: false,
})
export class EnableAccountInventoryComponent extends DomainBaseComponent {
  accounts: Account[] = [];
  selectedAccount: Account | null = null;

  // ---- settings entered once, written onto every selected order ----
  programNames: string[] = [];
  selectedProgramName: string | null = null;
  chargeAddonsOnReissuance = true;

  joHeaders: JoHeader[] = [];
  joDetails: JoDetail[] = [];
  johGridOptions: GridOptionsExt;

  rows: ProductTypeRow[] = [];
  styleGridOptions: GridOptionsExt;

  checkedCt = 0;

  isLoading = false;
  isLoadingDetails = false;
  hasRun = false;
  submitting = false;
  errorMessage: string | null = null;

  /** Orders whose JoDetails have already been fetched. */
  private loadedJoHeaderIds = new Set<number>();

  /** Styles the account stocks under the chosen program. Cached so re-ticking is synchronous. */
  private programProductTypeIds = new Set<number>();
  /** Typed prices survive a rebuild, so ticking another order does not wipe the grid. */
  private enteredPrices = new Map<number, number>();

  constructor(
    protected domainService: DomainService,
    private http: HttpClient,
  ) {
    super(domainService);

    this.johGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onJohGridReady,
      onCellValueChanged: this.onJohCellValueChanged,
      getRowId: (p: GetRowIdParams) => String((p.data as JoHeader).id),
      getRowClass: (p: any) => ((p.data as JoHeader)?.allowInventory ? 'rs-already-processed' : ''),
      cellSelection: false,
    });

    this.styleGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onStyleGridReady,
      onCellValueChanged: this.onStyleCellValueChanged,
      singleClickEdit: true,
      stopEditingWhenCellsLoseFocus: true,
      cellSelection: false,
      // Plain DTOs with no `id` — without this AgFns falls through to data.toString()
      // and every row gets the same key, which renders a ghost row.
      getRowId: (p: GetRowIdParams) => String((p.data as ProductTypeRow).productTypeId),
    });

    this.prepare();
  }

  async prepare() {
    this.accounts = await this.dbQueryService.getReissuanceAccounts();
    this.isPageReady = true;
  }

  get canApply() {
    return !this.isLoading && !this.submitting && this.checkedCt > 0;
  }

  get inProgramCount() {
    return this.rows.filter((row) => row.isInProgram).length;
  }

  @wrapBusy
  async onAccountChanged() {
    this.joHeaders = [];
    this.joDetails = [];
    this.rows = [];
    this.programNames = [];
    this.selectedProgramName = null;
    this.chargeAddonsOnReissuance = true;
    this.programProductTypeIds = new Set();
    this.enteredPrices = new Map();
    this.loadedJoHeaderIds = new Set();
    this.checkedCt = 0;
    this.errorMessage = null;
    this.hasRun = false;

    if (!this.selectedAccount) {
      return;
    }

    this.isLoading = true;
    try {
      const accountId = this.selectedAccount.id;
      this.programNames = await this.dbQueryService.getInventoryProgramNamesForAccount(accountId);
      if (this.programNames.length === 1) {
        this.selectedProgramName = this.programNames[0];
      }
      await this.loadProgramProductTypeIds();

      const johs = await this.dbQueryService.getJoHeadersForAccountInventoryRepair(accountId);
      this.joHeaders = _.orderBy(johs, ['id'], ['desc']);
      this.hasRun = true;

      if (this.joHeaders.length === 0) {
        this.toastr.info('The system found no open job orders for this account.', 'Enable Account Inventory');
      }
      // JoDetails load lazily, per ticked order. Some accounts have hundreds of open
      // orders and fetching every detail up front is both slow and unnecessary — the
      // style list only ever covers the selection.
    } finally {
      this.isLoading = false;
    }
  }

  /** Fetches details for newly ticked orders once, then keeps them for the session. */
  private async ensureDetailsLoaded(joHeaderIds: number[]) {
    const missing = joHeaderIds.filter((id) => !this.loadedJoHeaderIds.has(id));
    if (missing.length === 0) {
      return;
    }
    this.isLoadingDetails = true;
    try {
      const jods = await this.dbQueryService.getJoDetailsForJoHeaderIds(missing);
      this.joDetails.push(...jods);
      missing.forEach((id) => this.loadedJoHeaderIds.add(id));
    } finally {
      this.isLoadingDetails = false;
    }
  }

  private async loadProgramProductTypeIds() {
    if (!this.selectedProgramName) {
      this.programProductTypeIds = new Set();
      return;
    }
    const ids = await this.dbQueryService.getAccountOwnedProductTypeIds(this.selectedProgramName);
    this.programProductTypeIds = new Set(ids);
  }

  async onProgramNameChanged() {
    this.errorMessage = null;
    await this.loadProgramProductTypeIds();
    await this.rebuildStyleRows();
  }

  private getCheckedJoHeaders(): JoHeader[] {
    const checked: JoHeader[] = [];
    this.johGridOptions.apiX?.forEachNode((node) => {
      const joh = node.data as JoHeader;
      if (joh && (joh as any)._isEnableInvChecked) {
        checked.push(joh);
      }
    });
    return checked;
  }

  /** The style list is scoped to the ticked orders — price only what you are changing. */
  private async rebuildStyleRows() {
    const checked = this.getCheckedJoHeaders();
    this.checkedCt = checked.length;
    const checkedIds = new Set(checked.map((joh) => joh.id));

    await this.ensureDetailsLoaded(checked.map((joh) => joh.id));

    const scoped = this.joDetails.filter((jod) => checkedIds.has(jod.joHeaderId));
    const byProductType = _.groupBy(scoped, (jod) => jod.product?.productTypeId);

    this.rows = _.chain(Object.values(byProductType))
      .map((jods) => {
        const productType = jods[0].product?.productType;
        const productTypeId = productType?.id;
        // Lines of one style normally carry the same rate; take the highest so a
        // partially-priced group cannot read as fully unpriced.
        const currentReissueAmt = _.max(jods.map((jod) => jod.unitReissueAmt ?? 0)) ?? 0;
        return <ProductTypeRow>{
          productTypeId: productTypeId,
          manufacturerName: productType?.manufacturer?.name ?? '',
          style: productType?.style ?? '',
          description: productType?.description ?? '',
          lineCt: jods.length,
          unitCt: _.sumBy(jods, (jod) => jod.orderQty ?? 0),
          currentReissueAmt: currentReissueAmt,
          isInProgram: this.programProductTypeIds.has(productTypeId),
          // Defaults to the rate already on the lines, so applying without editing is a
          // no-op on price. A typed value wins; clearing the cell leaves the rate alone.
          newReissueAmt: this.enteredPrices.get(productTypeId) ?? currentReissueAmt,
        };
      })
      .filter((row) => row.productTypeId != null)
      .orderBy(['manufacturerName', 'style'])
      .value();

    await AgFns.refreshGrid(this.styleGridOptions, this.rows);
  }

  async onJohCellValueChanged(e: CellValueChangedEvent) {
    if (e.column.getColId() !== 'enableInvCheckbox') {
      return;
    }
    this.errorMessage = null;
    await this.rebuildStyleRows();
  }

  onStyleCellValueChanged(e: CellValueChangedEvent) {
    if (e.column.getColId() !== 'newReissueAmt') {
      return;
    }
    const row = e.data as ProductTypeRow;
    if (row.newReissueAmt == null) {
      this.enteredPrices.delete(row.productTypeId);
    } else {
      this.enteredPrices.set(row.productTypeId, row.newReissueAmt);
    }
    this.errorMessage = null;
  }

  onJohGridReady(event: GridReadyEvent) {
    const colDefs = [
      {
        ...AgFns.createCheckboxProps('', '_isEnableInvChecked', 'enableInvCheckbox'),
        valueGetter: (p: any) => !!p.data?._isEnableInvChecked,
        valueSetter: (p: any) => {
          if (!p.data) return false;
          p.data._isEnableInvChecked = !!p.newValue;
          return true;
        },
        editable: true,
        sortable: false,
        filter: false,
        ...AgFns.fixWidth(50),
      },
      {
        headerName: 'J/O #',
        ...NavFns.createIdCellClickedNavProps('id', this.router, '/in-process/manage/job-orders'),
        type: 'leftAligned',
        filter: 'agNumberColumnFilter',
        ...AgFns.fixWidth(120),
      },
      { headerName: 'J/O Date', field: 'joDate', filter: 'agDateColumnFilter', ...AgFns.fixWidth('date') },
      { headerName: 'J/O Status', field: 'joStatus.name', filter: 'agTextColumnFilter', ...AgFns.flexWidth(180) },
      { headerName: 'Ordered For', field: 'empLastName', filter: 'agTextColumnFilter', ...AgFns.flexWidth(140) },
      {
        headerName: 'Invoices',
        colId: 'invoiceCt',
        valueGetter: (p: any) => (p.data as JoHeader)?.invoiceHeaders?.length ?? 0,
        type: 'numericColumn',
        ...AgFns.fixWidth('qty'),
      },
      // Current state, read-only. The editable settings are the controls above.
      {
        ...AgFns.createCheckboxProps('Already Enabled?', 'allowInventory'),
        editable: false,
        ...AgFns.fixWidth(140),
      },
      {
        headerName: 'Current Inventory Name',
        field: 'inventoryProgramName',
        filter: 'agTextColumnFilter',
        ...AgFns.flexWidth(170),
      },
    ];
    AgFns.initGrid(event, colDefs, [{ colId: 'id', sort: 'desc' }]);
    AgFns.sizeColumnsToFit(this.johGridOptions);
  }

  onStyleGridReady(event: GridReadyEvent) {
    const colDefs = [
      { headerName: 'Manufacturer', field: 'manufacturerName', ...AgFns.flexWidth(140) },
      { headerName: 'Style', field: 'style', ...AgFns.flexWidth(110) },
      { headerName: 'Description', field: 'description', ...AgFns.flexWidth(200) },
      {
        ...AgFns.createCheckboxProps('In Program?', 'isInProgram', 'isInProgram'),
        editable: false,
        ...AgFns.fixWidth(110),
      },
      { headerName: 'Lines', field: 'lineCt', ...AgFns.fixWidth('qty') },
      { headerName: 'Units', field: 'unitCt', ...AgFns.fixWidth('qty') },
      { headerName: 'Current Reissue Amt', field: 'currentReissueAmt', ...AgFns.fixWidth('money') },
      {
        headerName: 'New Reissue Amt',
        field: 'newReissueAmt',
        colId: 'newReissueAmt',
        editable: true,
        valueParser: (p: any) => this.parsePriceOrNull(p.newValue),
        ...AgFns.fixWidth('money'),
      },
    ];
    AgFns.initGrid(event, colDefs, [{ colId: 'manufacturerName', sort: 'asc' }]);
    AgFns.sizeColumnsToFit(this.styleGridOptions);
  }

  /** Blank leaves the existing rate alone; anything unparsable is treated as blank. */
  private parsePriceOrNull(value: any): number | null {
    if (value == null || String(value).trim() === '') {
      return null;
    }
    const parsed = Number(String(value).replace(/[$,]/g, ''));
    if (isNaN(parsed) || parsed < 0) {
      return null;
    }
    return parsed;
  }

  @wrapBusy
  async apply() {
    if (this.submitting || !this.selectedAccount) {
      return;
    }
    this.styleGridOptions.apiX?.stopEditing();
    this.johGridOptions.apiX?.stopEditing();
    this.errorMessage = null;

    const checked = this.getCheckedJoHeaders();
    if (checked.length === 0) {
      this.errorMessage = 'Select at least one job order.';
      return;
    }

    if (!this.selectedProgramName || this.selectedProgramName.trim() === '') {
      this.errorMessage = 'Select or type an inventory program name.';
      return;
    }

    const missing = this.rows.filter((row) => row.isInProgram && row.newReissueAmt == null);
    if (missing.length > 0) {
      this.errorMessage = `Enter a new reissue amount for these styles: ${missing.map((r) => r.style).join(', ')}.`;
      return;
    }

    const priced = this.rows.filter((row) => row.newReissueAmt != null);
    if (priced.length === 0) {
      this.errorMessage = 'Enter a new reissue amount for at least one style.';
      return;
    }

    const invoiced = checked.filter((joh) => (joh.invoiceHeaders?.length ?? 0) > 0);
    if (invoiced.length > 0) {
      const ok = await this.dialogService.yesNoDialog(
        'Orders With Invoices',
        `These orders have an invoice: ${invoiced.map((joh) => joh.id).join(', ')}. ` +
          `The new reissue amount does not change an invoice that exists. Do you want to continue?`,
      );
      if (!ok) {
        return;
      }
    }

    this.submitting = true;
    try {
      const priceByProductTypeId = new Map(priced.map((row) => [row.productTypeId, row.newReissueAmt as number]));
      const programName = this.selectedProgramName.trim();
      const checkedIds = new Set(checked.map((joh) => joh.id));

      checked.forEach((joh) => {
        joh.allowInventory = true;
        joh.inventoryProgramName = programName;
        joh.chargeAddonsOnReissuance = this.chargeAddonsOnReissuance;
      });

      let changedDetailCt = 0;
      this.joDetails.forEach((jod) => {
        if (!checkedIds.has(jod.joHeaderId)) return;
        const amt = priceByProductTypeId.get(jod.product?.productTypeId);
        if (amt != null && jod.unitReissueAmt !== amt) {
          jod.unitReissueAmt = amt;
          changedDetailCt++;
        }
      });

      const ok = await this.dbSaveService.saveChanges();
      if (!ok) {
        // The validation dialog is already shown.
        return;
      }

      // The field changes are committed at this point. A failure below loses only the
      // audit note, so say so — running this again writes the same values a second time.
      try {
        await this.recordAuditNote(checked, programName, priced);
      } catch (err: any) {
        const detail = err?.error?.error || err?.message || 'unknown error';
        this.errorMessage =
          `The system saved the changes but could not write the note to the order history (${detail}). ` +
          `Do not do this again. Tell an administrator.`;
        return;
      }

      this.toastr.success(
        `Account inventory is on for ${checked.length} order(s). The system changed ${changedDetailCt} order line(s).`,
      );

      checked.forEach((joh) => ((joh as any)._isEnableInvChecked = false));
      this.enteredPrices = new Map();
      await AgFns.refreshGrid(this.johGridOptions, this.joHeaders);
      await this.rebuildStyleRows();
    } catch (err: any) {
      this.errorMessage = err?.error?.error || err?.message || 'The system could not save the changes.';
    } finally {
      this.submitting = false;
    }
  }

  /**
   * Breeze wrote the fields; the server owns the JoNote so the note is
   * system-generated and an operator cannot delete it.
   */
  private async recordAuditNote(checked: JoHeader[], programName: string, priced: ProductTypeRow[]) {
    const priceSummary = priced
      .map((row) => `${row.style} = ${UtilFns.fmtCurrency(row.newReissueAmt as number)}`)
      .join(', ');
    const url = environment.apiRoot + 'breeze/Uniformax/RecordAccountInventoryEnabled';
    await firstValueFrom(
      this.http.post(url, {
        joHeaderIds: checked.map((joh) => joh.id),
        programName: programName,
        priceSummary: priceSummary,
        actor: this.authUser?.initials ?? 'SYS',
      }),
    );
  }
}
